import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  Box,
  Typography,
  Button,
  Alert,
  Skeleton,
  Chip,
  Stack,
  Autocomplete,
  TextField,
  IconButton,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Tooltip,
  Link as MuiLink,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import {
  Star as LeadIcon,
  SupportAgent as AssistantIcon,
  Delete as DeleteIcon,
  PersonAdd as AddIcon,
  SwapHoriz as ReplaceIcon,
  MenuBook as SubjectIcon,
  School as SubjectTutorIcon,
  WarningAmber as WarningIcon,
  ScheduleOutlined as TimeIcon,
  PlaceOutlined as PlaceIcon,
} from "@mui/icons-material";
import { toast } from "react-toastify";
import api from "../../api";

/** Group roles, on `course_plan_staff.role`. The stored value stays "helper". */
const LEAD = "tutor";
const ASSISTANT = "helper";

/** Subject roles, on the `course_plan_staff_subjects` pivot. */
const MAIN = "main";
const ASSIST = "assistant";

/**
 * Staffing for one course plan: who runs each of its subjects, who assists, and when it is taught.
 *
 * The screen is SUBJECT-FIRST, because "who teaches English, and when?" is the question an admin
 * arrives with. Each subject row is the assignment, not a report of one made elsewhere.
 *
 * Two independent axes, which the old single-badge design conflated:
 *
 *   GROUP role   `lead` owns the group and approves content from anyone who does not run that
 *                subject. Exactly one per group. It is ownership, NOT a teaching rank — a lead who
 *                runs Maths teaches it like anybody else, and shows both badges.
 *   SUBJECT role each subject has one MAIN tutor, who hosts its classes and publishes its content,
 *                plus any number of ASSISTANTS, who help and may upload but cannot host it. The
 *                same person can assist on one subject and run another.
 *
 * The TIME is not chosen here. `course_subject` already stores a weekday, start time and duration
 * for every (subject, mode, location) the course runs, and a group is a mode and a location — so a
 * group's timetable is a lookup, and editing the course updates every group at once. The column is
 * read-only on purpose: making it editable would create a second source of truth for one fact.
 */

const ROLE_META = {
  lead: {
    label: "Lead tutor",
    icon: LeadIcon,
    color: "#2e7d32",
    bg: "#ecfdf5",
    help: "Owns this group and approves content from tutors who do not run that subject. One per group. They also teach the subjects assigned to them, like any other tutor.",
  },
  subject: {
    label: "Subject tutor",
    icon: SubjectTutorIcon,
    color: "#4338ca",
    bg: "#eef2ff",
    help: "Runs at least one subject here: hosts its classes and publishes its content without waiting on anyone.",
  },
  assistant: {
    label: "Assistant",
    icon: AssistantIcon,
    color: "#475569",
    bg: "#f1f5f9",
    help: "Runs no subject here. They can upload — to the subjects they assist on, or to the group generally — but the lead approves it and they cannot host a class.",
  },
};

/**
 * The badges one staff member wears. Ownership and teaching are separate facts, so a lead who runs
 * a subject gets both — which is what "the lead tutor is also a subject tutor" means.
 */
function roleKeysOf(member, isLead) {
  const runs = (member.subjects || []).some((s) => s.role !== ASSIST);
  const keys = [];
  if (isLead) keys.push("lead");
  if (runs) keys.push("subject");
  if (keys.length === 0) keys.push("assistant");
  return keys;
}

function RoleBadge({ roleKey }) {
  const meta = ROLE_META[roleKey];
  const Icon = meta.icon;
  return (
    <Tooltip title={meta.help}>
      <Chip
        size="small"
        icon={<Icon sx={{ fontSize: 14, color: `${meta.color} !important` }} />}
        label={meta.label}
        sx={{ bgcolor: meta.bg, color: meta.color, fontWeight: 700 }}
      />
    </Tooltip>
  );
}

const DAY_SHORT = {
  Monday: "Mon",
  Tuesday: "Tue",
  Wednesday: "Wed",
  Thursday: "Thu",
  Friday: "Fri",
  Saturday: "Sat",
  Sunday: "Sun",
};

const scheduleLabel = (s) =>
  s && s.start_time
    ? `${DAY_SHORT[s.weekday_name] || s.weekday_name || "?"} ${s.start_time}${s.end_time ? `–${s.end_time}` : ""}`
    : "";

function formatLoad(minutes) {
  if (!minutes) return null;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h && m) return `${h}h ${m}m`;
  return h ? `${h}h` : `${m}m`;
}

/**
 * Searchable tutor picker showing each candidate's login role and current group load.
 *
 * The list can run to every tutor on the platform, so a plain Select means scrolling. The group
 * count is here so an admin can see who is already stretched *before* assigning.
 */
function TutorPicker({ options, value, onChange, disabled, label, helper }) {
  return (
    <Autocomplete
      fullWidth
      size="small"
      disabled={disabled}
      options={options}
      value={options.find((t) => t.id === value) ?? null}
      onChange={(_, picked) => onChange(picked?.id ?? "")}
      isOptionEqualToValue={(opt, val) => opt.id === val?.id}
      getOptionLabel={(t) => `${t.name} — ${t.email}`}
      renderOption={(props, t) => (
        <Box component="li" {...props} key={t.id}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" fontWeight={600} noWrap>
              {t.name}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap>
              {t.email}
            </Typography>
          </Box>
          <Stack direction="row" spacing={0.5} sx={{ ml: 1, flexShrink: 0 }}>
            {/* What their profile lets them teach on this course — what they would arrive with. */}
            <Chip
              size="small"
              variant="outlined"
              color={(t.course_subject_ids || []).length === 0 ? "warning" : "default"}
              label={
                (t.course_subject_ids || []).length === 0
                  ? "No course subjects"
                  : `${t.course_subject_ids.length} subject${t.course_subject_ids.length === 1 ? "" : "s"}`
              }
            />
            {t.role_name && t.role_name !== "Tutor" && (
              <Chip size="small" variant="outlined" label={t.role_name} />
            )}
            {t.group_count > 0 && (
              <Chip
                size="small"
                label={`${t.group_count} group${t.group_count === 1 ? "" : "s"}`}
                sx={{ bgcolor: "#f1f5f9", fontWeight: 600 }}
              />
            )}
          </Stack>
        </Box>
      )}
      renderInput={(params) => <TextField {...params} label={label} helperText={helper} />}
    />
  );
}

/**
 * A tutor option inside a subject row, showing what they already hold here.
 *
 * Seeing someone's existing load at the moment of choosing is the point: an admin should not have
 * to remember that Emily already runs English before handing her Maths as well.
 */
function tutorOptionSummary(t) {
  const runs = (t.group_subjects || []).filter((s) => s.role !== ASSIST).map((s) => s.name);
  const assists = (t.group_subjects || []).filter((s) => s.role === ASSIST).map((s) => s.name);
  const bits = [];
  if (runs.length) bits.push(`runs ${runs.join(", ")}`);
  if (assists.length) bits.push(`assists ${assists.join(", ")}`);
  if (!t.on_group) return "Not on this group yet";
  return bits.join(" · ") || "On the group, no subject yet";
}

/**
 * One subject: who runs it, who assists, and when it is taught.
 *
 * An uncovered subject is amber — students with nobody to host a class is the failure this screen
 * exists to surface. The time is display-only, straight from the course.
 */
function SubjectRow({
  subject,
  tutorOptions,
  courseId,
  onSetMain,
  onAddAssistant,
  onRemoveTutor,
  busy,
}) {
  const main = subject.main_tutor || null;
  const assistants = subject.assistants || [];
  const schedule = subject.schedule || null;
  const uncovered = !main;
  const [addingAssistant, setAddingAssistant] = useState(false);

  // Only tutors whose profile allows this subject on this course (course_subject_tutor). Offering
  // more would let an admin pick something the server refuses, and hosting would fail later anyway
  // on the course-level check in ClassroomController.
  const eligible = useMemo(
    () =>
      tutorOptions
        .filter((t) => (t.course_subject_ids || []).map(String).includes(String(subject.subject_id)))
        // Best choice first: already on the group, then everyone else.
        .sort((a, b) => Number(b.on_group) - Number(a.on_group)),
    [tutorOptions, subject.subject_id]
  );

  const assistantOptions = useMemo(
    () =>
      eligible.filter(
        (t) => t.id !== main?.user_id && !assistants.some((a) => a.user_id === t.id)
      ),
    [eligible, main, assistants]
  );

  return (
    <Box
      sx={{
        p: 1.25,
        borderRadius: 1.5,
        border: "1px solid",
        borderColor: uncovered ? "warning.light" : "divider",
        bgcolor: uncovered ? "#fffbeb" : "transparent",
      }}
    >
      <Stack direction={{ xs: "column", md: "row" }} alignItems={{ md: "center" }} spacing={1.5}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 165 }}>
          <SubjectIcon sx={{ fontSize: 16, color: uncovered ? "#b45309" : "#4338ca" }} />
          <Typography variant="body2" fontWeight={700}>
            {subject.subject_name}
          </Typography>
        </Stack>

        {/* Main tutor — the person who runs this subject. */}
        <Box sx={{ flex: 1, minWidth: 200 }}>
          <Autocomplete
            size="small"
            disabled={busy}
            options={eligible}
            value={eligible.find((t) => t.id === main?.user_id) ?? null}
            onChange={(_, picked) => onSetMain(subject, picked)}
            isOptionEqualToValue={(opt, val) => opt.id === val?.id}
            getOptionLabel={(t) => t.name || ""}
            renderOption={(props, t) => (
              <Box component="li" {...props} key={t.id}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" noWrap>
                    {t.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {tutorOptionSummary(t)}
                  </Typography>
                </Box>
              </Box>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Main tutor"
                placeholder={eligible.length === 0 ? "No eligible tutor" : "Nobody assigned"}
                error={uncovered}
              />
            )}
            noOptionsText="No tutor on this course teaches this subject"
          />
        </Box>

        {/* Time — read-only. The course already says when this runs for this group. */}
        <Box sx={{ minWidth: 200 }}>
          {schedule && schedule.start_time ? (
            <Stack direction="row" spacing={0.75} alignItems="center">
              <TimeIcon sx={{ fontSize: 16, color: "#0277bd" }} />
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" fontWeight={600} noWrap>
                  {scheduleLabel(schedule)}
                </Typography>
                <Stack direction="row" spacing={0.5} alignItems="center">
                  {schedule.duration_minutes && (
                    <Typography variant="caption" color="text.secondary">
                      {schedule.duration_minutes} min
                    </Typography>
                  )}
                  {schedule.location_name && (
                    <>
                      <PlaceIcon sx={{ fontSize: 12, color: "#94a3b8" }} />
                      <Typography variant="caption" color="text.secondary" noWrap>
                        {schedule.location_name}
                      </Typography>
                    </>
                  )}
                </Stack>
                {/* This group's own venue has no row, so the time came from another. Say so. */}
                {!schedule.exact && (
                  <Typography variant="caption" sx={{ color: "#b45309" }}>
                    from another venue
                  </Typography>
                )}
              </Box>
            </Stack>
          ) : (
            <Typography variant="caption" color="text.secondary">
              Not scheduled on the course
            </Typography>
          )}
        </Box>

        <Box sx={{ minWidth: 34 }}>
          {main && (
            <Tooltip title={`Remove ${main.name} from ${subject.subject_name}`}>
              <span>
                <IconButton
                  size="small"
                  color="error"
                  disabled={busy}
                  onClick={() => onRemoveTutor(subject, main.user_id)}
                >
                  <DeleteIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </span>
            </Tooltip>
          )}
        </Box>
      </Stack>

      {/* Assistants on this subject — any number, none of whom may host it. */}
      {main && (
        <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mt: 1, pl: { md: 21 } }}>
          <Typography variant="caption" color="text.secondary">
            Assistants:
          </Typography>
          {assistants.length === 0 && !addingAssistant && (
            <Typography variant="caption" color="text.secondary">
              none
            </Typography>
          )}
          {assistants.map((a) => (
            <Chip
              key={a.user_id}
              size="small"
              icon={<AssistantIcon sx={{ fontSize: 13 }} />}
              label={a.name}
              onDelete={busy ? undefined : () => onRemoveTutor(subject, a.user_id)}
              sx={{ bgcolor: "#f1f5f9", color: "#475569", fontWeight: 600 }}
            />
          ))}
          {addingAssistant ? (
            <Autocomplete
              size="small"
              sx={{ minWidth: 240 }}
              open
              disabled={busy}
              options={assistantOptions}
              getOptionLabel={(t) => t.name || ""}
              isOptionEqualToValue={(opt, val) => opt.id === val?.id}
              onChange={(_, picked) => {
                setAddingAssistant(false);
                if (picked) onAddAssistant(subject, picked);
              }}
              onBlur={() => setAddingAssistant(false)}
              renderOption={(props, t) => (
                <Box component="li" {...props} key={t.id}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" noWrap>
                      {t.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" noWrap>
                      {tutorOptionSummary(t)}
                    </Typography>
                  </Box>
                </Box>
              )}
              renderInput={(params) => <TextField {...params} autoFocus placeholder="Add assistant…" />}
              noOptionsText="No other tutor on this course teaches this subject"
            />
          ) : (
            <Button
              size="small"
              disabled={busy || assistantOptions.length === 0}
              onClick={() => setAddingAssistant(true)}
              sx={{ px: 0.75, minWidth: 0, textTransform: "none" }}
            >
              + Add
            </Button>
          )}
        </Stack>
      )}

      {/* The dead end this screen used to hit silently: nobody on the course teaches this subject. */}
      {eligible.length === 0 && (
        <Alert severity="warning" icon={false} sx={{ mt: 1, py: 0.25 }}>
          <Typography variant="caption">
            No tutor on this course is assigned <strong>{subject.subject_name}</strong>. It is granted
            under “Assign Specific Courses” on a tutor's profile —{" "}
            <MuiLink component={RouterLink} to="/admin/tutor-list">
              open the tutor list
            </MuiLink>
            {courseId ? " and tick this subject for whoever should teach it." : "."}
          </Typography>
        </Alert>
      )}
    </Box>
  );
}

/**
 * One staff member: their badges, everything they hold here, and what that adds up to.
 *
 * The weekly load line is the multi-subject view the tutor-first layout could not show — a tutor
 * running Maths and English appeared once per subject with nothing saying they carried both.
 */
function StaffCard({ member, isLead, schedulesBySubject, onRemove, onGiveSubject, removeDisabledReason, busy }) {
  const subjects = member.subjects || [];
  const runs = subjects.filter((s) => s.role !== ASSIST);
  const assists = subjects.filter((s) => s.role === ASSIST);
  const keys = roleKeysOf(member, isLead);

  // Hours are what they RUN: assisting is not a teaching slot of their own.
  const load = formatLoad(
    runs.reduce((sum, s) => sum + (schedulesBySubject[s.id]?.duration_minutes || 0), 0)
  );

  const chipFor = (s, assisting) => (
    <Chip
      key={`${assisting ? "a" : "m"}-${s.id}`}
      size="small"
      icon={assisting ? <AssistantIcon sx={{ fontSize: 13 }} /> : <SubjectIcon sx={{ fontSize: 14 }} />}
      label={
        scheduleLabel(schedulesBySubject[s.id])
          ? `${s.name} · ${scheduleLabel(schedulesBySubject[s.id])}`
          : s.name
      }
      variant={assisting ? "outlined" : "filled"}
      sx={
        assisting
          ? { color: "#64748b", fontWeight: 600 }
          : { bgcolor: "#eef2ff", color: "#4338ca", fontWeight: 600 }
      }
    />
  );

  return (
    <Paper variant="outlined" sx={{ p: 1.75, borderRadius: 2 }}>
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2}>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography fontWeight={600}>{member.name}</Typography>
            {keys.map((k) => (
              <RoleBadge key={k} roleKey={k} />
            ))}
          </Stack>
          <Typography variant="body2" color="text.secondary">
            {member.email}
          </Typography>

          {subjects.length === 0 ? (
            <Box sx={{ mt: 1 }}>
              <Typography variant="caption" color="text.secondary" display="block">
                {isLead
                  ? "Runs no subject yet — as lead they cover everything, which is how an unsplit group works."
                  : ROLE_META.assistant.help}
              </Typography>
              {!isLead && (
                <Button size="small" onClick={onGiveSubject} sx={{ mt: 0.5, px: 0 }}>
                  Give them a subject
                </Button>
              )}
            </Box>
          ) : (
            <>
              <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                {runs.map((s) => chipFor(s, false))}
                {assists.map((s) => chipFor(s, true))}
              </Stack>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75, display: "block" }}>
                {runs.length > 0
                  ? `Runs ${runs.length} subject${runs.length === 1 ? "" : "s"}${load ? ` · ${load}/week` : ""}`
                  : "Runs nothing"}
                {assists.length > 0 ? ` · assists on ${assists.length}` : ""}
              </Typography>
            </>
          )}
        </Box>

        <Tooltip title={removeDisabledReason || `Remove ${member.name} from this group`}>
          <span>
            <IconButton color="error" disabled={busy || Boolean(removeDisabledReason)} onClick={onRemove}>
              <DeleteIcon />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>
    </Paper>
  );
}

export default function StaffAssignmentTab({ selectedPlan }) {
  const planId = selectedPlan?.id;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [lead, setLead] = useState(null);
  const [assistants, setAssistants] = useState([]);
  const [tutors, setTutors] = useState([]);
  const [planSubjects, setPlanSubjects] = useState([]);
  const [unassigned, setUnassigned] = useState([]);
  const [courseId, setCourseId] = useState(null);

  // Set when taking a subject from the tutor who runs it — offered, never silent.
  const [reassignTarget, setReassignTarget] = useState(null);

  const [leadChoice, setLeadChoice] = useState("");
  const [assistantChoice, setAssistantChoice] = useState("");
  const [replaceTarget, setReplaceTarget] = useState(null);

  const subjectTableRef = useRef(null);

  const load = useCallback(() => {
    if (!planId) return;
    setLoading(true);
    setError("");
    api
      .get(`admin/course-plans/${planId}/staff`)
      .then((res) => {
        const d = res.data?.data ?? {};
        setLead(d.lead_tutor ?? null);
        // The API key is still `helpers` — only the label changed, not the wire contract.
        setAssistants(Array.isArray(d.helpers) ? d.helpers : []);
        setTutors(Array.isArray(d.available_tutors) ? d.available_tutors : []);
        setPlanSubjects(Array.isArray(d.plan_subjects) ? d.plan_subjects : []);
        setUnassigned(Array.isArray(d.unassigned_subjects) ? d.unassigned_subjects : []);
        setCourseId(d.course_id ?? null);
      })
      .catch((e) => setError(e?.response?.data?.message || "Could not load staffing for this plan."))
      .finally(() => setLoading(false));
  }, [planId]);

  useEffect(() => {
    load();
    setLeadChoice("");
    setAssistantChoice("");
  }, [load]);

  const staffList = useMemo(
    () =>
      (lead ? [{ member: lead, isLead: true }] : []).concat(
        assistants.map((h) => ({ member: h, isLead: false }))
      ),
    [lead, assistants]
  );

  const staffByUserId = useMemo(() => {
    const m = new Map();
    staffList.forEach(({ member, isLead }) => m.set(member.user_id, { member, isLead }));
    return m;
  }, [staffList]);

  /** The course's schedule per subject, for the roster cards' chips and hour totals. */
  const schedulesBySubject = useMemo(() => {
    const m = {};
    planSubjects.forEach((s) => {
      if (s.schedule) m[s.subject_id] = s.schedule;
    });
    return m;
  }, [planSubjects]);

  /** Options for a subject row, carrying what each tutor already holds in this group. */
  const rowTutorOptions = useMemo(
    () =>
      tutors.map((t) => ({
        ...t,
        on_group: staffByUserId.has(t.id),
        group_subjects: staffByUserId.get(t.id)?.member.subjects || [],
      })),
    [tutors, staffByUserId]
  );

  /** Add a tutor to the group if they are not on it yet. Returns false when that failed. */
  const ensureOnGroup = useCallback(
    async (userId) => {
      if (staffByUserId.has(userId)) return true;
      try {
        // subject_ids is deliberately not sent: the server fills them in from the tutor's course
        // profile, skipping anything another tutor already runs.
        await api.post(`admin/course-plans/${planId}/staff`, {
          user_id: userId,
          role: ASSISTANT,
          replace_existing: false,
        });
        return true;
      } catch (e) {
        toast.error(
          e?.response?.data?.errors?.user_id?.[0] ||
            e?.response?.data?.message ||
            "Could not add this tutor to the group."
        );
        return false;
      }
    },
    [planId, staffByUserId]
  );

  /**
   * Set a subject's role for one tutor.
   *
   * A 422 naming another holder is the one-main-per-subject rule; it is offered as a confirm rather
   * than applied, the same shape as replacing a lead.
   */
  const setSubjectRole = useCallback(
    async (subject, picked, role, reassign = false) => {
      setSaving(true);
      try {
        const ok = await ensureOnGroup(picked.id);
        if (!ok) return;

        await api.put(`admin/course-plans/${planId}/staff/${picked.id}/subject-tutor`, {
          subject_id: subject.subject_id,
          role,
          reassign,
        });
        toast.success(
          role === MAIN
            ? `${picked.name} now runs ${subject.subject_name}.`
            : `${picked.name} assists on ${subject.subject_name}.`
        );
        setReassignTarget(null);
        load();
      } catch (e) {
        const msg = e?.response?.data?.errors?.subject_id?.[0];
        if (msg && msg.startsWith("Already covered in this group")) {
          setReassignTarget({ message: msg, subject, picked, role });
          return;
        }
        toast.error(msg || e?.response?.data?.message || "Could not assign this subject.");
      } finally {
        setSaving(false);
      }
    },
    [planId, ensureOnGroup, load]
  );

  const handleSetMain = useCallback(
    async (subject, picked) => {
      const current = subject.main_tutor;
      if (!picked) {
        if (current) await removeSubjectTutor(subject, current.user_id);
        return;
      }
      if (current && current.user_id === picked.id) return;
      await setSubjectRole(subject, picked, MAIN);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setSubjectRole]
  );

  /** Take one subject off one tutor. They stay on the group — removing them is a separate action. */
  const removeSubjectTutor = useCallback(
    async (subject, userId) => {
      setSaving(true);
      try {
        await api.delete(`admin/course-plans/${planId}/staff/${userId}/subject-tutor`, {
          data: { subject_id: subject.subject_id },
        });
        toast.success(`${subject.subject_name} unassigned.`);
        load();
      } catch (e) {
        toast.error(e?.response?.data?.message || "Could not unassign this subject.");
      } finally {
        setSaving(false);
      }
    },
    [planId, load]
  );

  const assign = async (userId, role, replaceExisting = false) => {
    setSaving(true);
    try {
      const res = await api.post(`admin/course-plans/${planId}/staff`, {
        user_id: userId,
        role,
        replace_existing: replaceExisting,
      });
      toast.success(res.data?.message || (role === LEAD ? "Lead tutor assigned." : "Tutor added."));
      setLeadChoice("");
      setAssistantChoice("");
      load();
    } catch (e) {
      const msg =
        e?.response?.data?.errors?.user_id?.[0] ||
        e?.response?.data?.message ||
        "Could not assign this staff member.";
      toast.error(msg);
    } finally {
      setSaving(false);
      setReplaceTarget(null);
    }
  };

  const remove = async (userId, role) => {
    setSaving(true);
    try {
      await api.delete(`admin/course-plans/${planId}/staff/${userId}`, { data: { role } });
      toast.success("Staff member removed.");
      load();
    } catch (e) {
      const msg =
        e?.response?.data?.errors?.user_id?.[0] ||
        e?.response?.data?.message ||
        "Could not remove this staff member.";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleLeadSubmit = () => {
    if (!leadChoice) return;
    if (lead) {
      setReplaceTarget(leadChoice);
      return;
    }
    assign(leadChoice, LEAD);
  };

  if (!planId) {
    return <Alert severity="info">Select a course plan to manage its staff.</Alert>;
  }

  if (loading) return <Skeleton variant="rounded" height={280} />;

  const assignedIds = new Set([lead?.user_id, ...assistants.map((h) => h.user_id)].filter(Boolean));
  const addTutorOptions = tutors.filter((t) => !assignedIds.has(t.id));
  const leadOptions = tutors.filter((t) => t.id !== lead?.user_id);
  const covered = planSubjects.length - unassigned.length;

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* ── Who teaches what, and when ─────────────────────────────────
          One row per subject: the tutor who runs it, the assistants helping, and the time the
          course already defines for this group. Gaps are a warning, never a block. */}
      <Paper variant="outlined" sx={{ p: 2, mb: 3, borderRadius: 2 }} ref={subjectTableRef}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ sm: "center" }}
          spacing={1}
        >
          <Typography variant="subtitle1" fontWeight={700} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <SubjectIcon fontSize="small" sx={{ color: "#4338ca" }} /> Who teaches what, and when
          </Typography>
          {planSubjects.length > 0 && (
            <Chip
              size="small"
              variant="outlined"
              color={unassigned.length === 0 ? "success" : "warning"}
              label={`${covered} of ${planSubjects.length} subject${planSubjects.length === 1 ? "" : "s"} covered`}
            />
          )}
        </Stack>
        <Typography variant="caption" color="text.secondary">
          Each subject has one <strong>main tutor</strong>, who hosts its classes and publishes its
          content, plus any number of <strong>assistants</strong>, who help and may upload but cannot
          host it. A tutor can run several subjects. The times come from the course's own per-subject
          schedule for this group's mode and location — edit them on the course, not here.
        </Typography>

        {!lead && (
          <Alert severity="warning" icon={<WarningIcon />} sx={{ mt: 1.5 }}>
            This group has no lead tutor. Assign one below — the group needs an owner before other
            tutors can join, so the rows here cannot be filled in yet.
          </Alert>
        )}

        {planSubjects.length === 0 ? (
          <Alert severity="info" sx={{ mt: 1.5 }}>
            This group's course has no subjects set up yet, so there is nothing to staff.
          </Alert>
        ) : (
          <Stack spacing={1} sx={{ mt: 1.5 }}>
            {planSubjects.map((s) => (
              <SubjectRow
                key={s.subject_id}
                subject={s}
                tutorOptions={rowTutorOptions}
                courseId={courseId}
                busy={saving || !lead}
                onSetMain={handleSetMain}
                onAddAssistant={(subject, picked) => setSubjectRole(subject, picked, ASSIST)}
                onRemoveTutor={removeSubjectTutor}
              />
            ))}
          </Stack>
        )}
      </Paper>

      {/* ── Tutors on this group ─────────────────────────────────────── */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ sm: "center" }}
        spacing={1}
        sx={{ mb: 0.5 }}
      >
        <Typography variant="subtitle1" fontWeight={700} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <LeadIcon fontSize="small" sx={{ color: "#2e7d32" }} /> Tutors on this group
        </Typography>
        <Stack direction="row" spacing={0.75}>
          {Object.keys(ROLE_META).map((k) => (
            <RoleBadge key={k} roleKey={k} />
          ))}
        </Stack>
      </Stack>
      <Typography variant="caption" color="text.secondary">
        Hover a badge for what it means. Owning the group and teaching a subject are separate things,
        so a lead who runs a subject carries both — assign subjects in the table above, not here.
      </Typography>

      <Stack spacing={1.25} sx={{ mt: 1.5 }}>
        {staffList.length === 0 ? (
          <Alert severity="warning">No lead tutor yet. Assign one before adding other tutors.</Alert>
        ) : (
          staffList.map(({ member, isLead }) => (
            <StaffCard
              key={member.id}
              member={member}
              isLead={isLead}
              schedulesBySubject={schedulesBySubject}
              busy={saving}
              removeDisabledReason={
                isLead && assistants.length > 0
                  ? "Remove or reassign the other tutors before removing the lead tutor"
                  : ""
              }
              onRemove={() => remove(member.user_id, isLead ? LEAD : ASSISTANT)}
              onGiveSubject={() =>
                subjectTableRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })
              }
            />
          ))
        )}
      </Stack>

      <Paper variant="outlined" sx={{ p: 2, mt: 1.5, borderRadius: 2, bgcolor: "#fafafa" }}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={1.5}>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ flex: 1 }}>
            <TutorPicker
              options={leadOptions}
              value={leadChoice}
              onChange={setLeadChoice}
              disabled={saving}
              label={lead ? "Replace lead tutor with" : "Select lead tutor"}
              helper="Their course subjects are assigned to this group automatically."
            />
            <Button
              variant="contained"
              startIcon={lead ? <ReplaceIcon /> : <AddIcon />}
              disabled={!leadChoice || saving}
              onClick={handleLeadSubmit}
              sx={{ whiteSpace: "nowrap", height: 40 }}
            >
              {lead ? "Replace" : "Assign lead"}
            </Button>
          </Stack>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ flex: 1 }}>
            <TutorPicker
              options={addTutorOptions}
              value={assistantChoice}
              onChange={setAssistantChoice}
              disabled={!lead || saving}
              label="Add another tutor"
              helper={
                lead
                  ? "Their course subjects are assigned to this group automatically."
                  : "Assign a lead tutor first — the group needs an owner."
              }
            />
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              disabled={!lead || !assistantChoice || saving}
              onClick={() => assign(assistantChoice, ASSISTANT)}
              sx={{ whiteSpace: "nowrap", height: 40 }}
            >
              Add
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {/* Taking a subject off the tutor who runs it is explicit, never silent. */}
      <Dialog open={Boolean(reassignTarget)} onClose={() => setReassignTarget(null)}>
        <DialogTitle>Move this subject?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {reassignTarget?.message} One tutor runs each subject, so moving it here takes it from
            whoever runs it now. Their other subjects, and this subject's assistants, are untouched.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReassignTarget(null)} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="warning"
            disabled={saving}
            onClick={() =>
              setSubjectRole(reassignTarget.subject, reassignTarget.picked, reassignTarget.role, true)
            }
          >
            {saving ? "Moving…" : "Move it"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Replacing a lead is explicit, never silent. */}
      <Dialog open={Boolean(replaceTarget)} onClose={() => setReplaceTarget(null)}>
        <DialogTitle>Replace lead tutor?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            <strong>{lead?.name}</strong> will be removed as lead tutor of this group and replaced by{" "}
            <strong>{tutors.find((t) => t.id === replaceTarget)?.name}</strong>.
            {assistants.length > 0 && (
              <>
                {" "}
                The {assistants.length} other tutor{assistants.length === 1 ? "" : "s"} stay assigned,
                keep their subjects, and work under the new lead.
              </>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReplaceTarget(null)}>Cancel</Button>
          <Button variant="contained" color="warning" onClick={() => assign(replaceTarget, LEAD, true)}>
            Replace
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
